import {
  Box,
  IconButton,
  MenuItem,
  Select,
  SelectProps,
  Typography,
  OutlinedInput,
} from "@mui/material";
import ClearIcon from "@mui/icons-material/Clear";
import { FieldErrors } from "react-hook-form";
import isEmpty from "lodash/isEmpty";

import styles from "./selectinput.module.css";
import { colors } from "../../theme/colors";

interface SelectItem {
  id: number;
  label: string;
  value: string | number;
}

interface SelectInputProps extends SelectProps {
  register?: any;
  label?: string;
  errors?: FieldErrors;
  data?: SelectItem[];
  value?: SelectItem | string | number | null;
  disabled?: boolean;
  onClear?: () => void;
}

const SingleSelectInput = (props: SelectInputProps) => {
  const {
    label,
    data,
    placeholder,
    required,
    register,
    name = "",
    error,
    errors = {},
    value,
    disabled = false,
    onClear,
    ...rest
  } = props;

  const hasValue = value !== null && value !== undefined && value !== "";

  return (
    <Box sx={{ flex: 1, flexDirection: "column" }}>
      {label && <Typography className={styles.inputLabel} sx={{ display: "flex" }}>
        {label}
        {required && (
          <Typography sx={{ color: "red", marginLeft: 0.7 }}>{" *"}</Typography>
        )}
      </Typography>}
      <Select
        sx={{
          display: "flex",
          border: `1px solid ${errors[name] ? colors.red : colors.white}`,
          "& .MuiSelect-icon": {
            right: 7,
          },
        }}
        {...rest}
        id="select-helper"
        displayEmpty
        {...register?.(name)}
        input={<OutlinedInput />}
        value={value}
        endAdornment={
          onClear && (
            <IconButton
              size="small"
              aria-label="Clear selection"
              sx={{
                position: "absolute",
                right: 32,
                visibility: hasValue && !disabled ? "visible" : "hidden",
              }}
              onMouseDown={(e) => e.preventDefault()}
              onClick={(e) => {
                e.stopPropagation();
                onClear();
              }}
            >
              <ClearIcon fontSize="small" />
            </IconButton>
          )
        }
        renderValue={(selected: SelectItem | string | number) => {
          // lodash isEmpty() is true for every number, so only apply it to objects
          const isBlank =
            selected === null ||
            selected === undefined ||
            selected === "" ||
            (typeof selected === "object" && isEmpty(selected));
          if (isBlank) {
            return (
              <Typography sx={{ color: colors.grey }}>{placeholder}</Typography>
            );
          }
          if (data && typeof selected !== "object") {
            const match = data.find((item) => item.value === selected);
            return match?.label ?? selected;
          }
          return selected as string;
        }}
        error={error}
        disabled={disabled}
        MenuProps={{
          PaperProps: {
            style: {
              maxHeight: 350, // Limit the dropdown height
              overflow: "auto", // Enable scrolling
            },
          },
        }}
      >
        {data?.map((item) => (
          <MenuItem key={item.id} value={item.value}>
            {item.label}
          </MenuItem>
        ))}
      </Select>
      {
        errors[name] && (
          <Typography sx={{ color: colors.red, fontSize: 12, marginTop: 0.5 }}>
            {errors[name].message?.toString() ?? ""}
          </Typography>
        )
      }
    </Box >
  );
}

export default SingleSelectInput;
